import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Loader2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { requirementsService, type RequirementCategory } from '@/services/requirements.service';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

interface FormValues {
  name: string;
  code: string;
  description: string;
}

interface Props {
  open: boolean;
  mode: 'create' | 'edit';
  category?: RequirementCategory;
  onClose: () => void;
  onSuccess: () => void;
}

export function CategoryFormDialog({ open, mode, category, onClose, onSuccess }: Props) {
  const isEdit = mode === 'edit';
  const queryClient = useQueryClient();

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm<FormValues>({
    defaultValues: { name: '', code: '', description: '' },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: category?.name ?? '',
        code: category?.code ?? '',
        description: category?.description ?? '',
      });
    }
  }, [open, category, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const input = {
        name: values.name.trim(),
        code: values.code.trim().toUpperCase(),
        description: values.description.trim() || null,
      };
      if (isEdit && category) {
        await requirementsService.updateCategory(category.id, input);
      } else {
        await requirementsService.createCategory(input);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Category updated' : 'Category created');
      void queryClient.invalidateQueries({ queryKey: ['requirements', 'categories'] });
      onSuccess();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-full max-w-md" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Category' : 'New Category'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="req-cat-name">Category Name *</Label>
            <Input
              id="req-cat-name"
              {...register('name', { required: 'Name is required' })}
              placeholder="e.g. Hardware"
            />
            {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-cat-code">Category Code *</Label>
            <Input
              id="req-cat-code"
              {...register('code', { required: 'Code is required', maxLength: { value: 20, message: 'Max 20 characters' } })}
              onChange={(e) => setValue('code', e.target.value.toUpperCase())}
              placeholder="e.g. HARDWARE"
              maxLength={20}
            />
            {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="req-cat-desc">Description</Label>
            <Textarea
              id="req-cat-desc"
              {...register('description')}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
